import React from 'react';

import { Tooltip } from "@material-ui/core";
import { getDisplayName } from "../../deviceHelpers";
import { getFormattedTimeString } from "../../time";
import { DATA_SCOPES, FILTER_TYPE } from "../filterConstants";
import { DateFilter } from "../filterTypes/dateFilter";
import { ElementFilter } from "../filterTypes/elementFilter";
import { NumberFilter } from "../filterTypes/numberFilter";
import { StringFilter } from "../filterTypes/stringFilter";
import { DEVICE_UPDATE_STATUS, MINIMUM_BATTERY_VOLTAGE } from "../../constants";

import PendingIcon from '@material-ui/icons/CachedOutlined';
import PassedIcon from '@material-ui/icons/CheckCircleOutline';
import FailedIcon from '@material-ui/icons/HighlightOffOutlined';
import NoActionIcon from '@material-ui/icons/RemoveCircleOutline';
import BatteryFullIcon from '@material-ui/icons/BatteryFull';
import Battery80Icon from '@material-ui/icons/Battery80';
import Battery60Icon from '@material-ui/icons/Battery60';
import Battery50Icon from '@material-ui/icons/Battery50';
import Battery30Icon from '@material-ui/icons/Battery30';
import LowBatteryIcon from '@material-ui/icons/Battery20';
import InsightMapDialog from '../../../components/UserInsights/InsightMapDialog';
import { ListFilter } from '../filterTypes/listFilter';

export class AllowWifiInsightsFilter extends NumberFilter {
  constructor(visibility, restrictedToScope) {
    super("Allow Wifi Insights", FILTER_TYPE.DEVICE.WIFI_INSIGHTS, [DATA_SCOPES.DEVICE], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.allow_wifi_insights;
  }
}

export class AllowWifiImagesFilter extends NumberFilter {
  constructor(visibility, restrictedToScope) {
    super("Allow Wifi Images", FILTER_TYPE.DEVICE.WIFI_IMAGES, [DATA_SCOPES.DEVICE], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.allow_complete_images;
  }
}

export class LoraIdFilter extends StringFilter {
  constructor(visibility, restrictedToScope) {
    super("Lora ID", FILTER_TYPE.DEVICE.LORA_ID, [DATA_SCOPES.DEVICE], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.lora_uid;
  }
}

export class CodeVersionFilter extends StringFilter {
  constructor(visibility, restrictedToScope) {
    super("Firmware Version", FILTER_TYPE.DEVICE.CODE_VERSION, [DATA_SCOPES.DEVICE], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.software_version;
  }
}

export class MacIdFilter extends StringFilter {
  constructor(visibility, restrictedToScope) {
    super("MAC ID", FILTER_TYPE.DEVICE.MAC_ID, [DATA_SCOPES.DEVICE], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.hardware_id;
  }
}

export class McuVersionFilter extends StringFilter {
  constructor(visibility, restrictedToScope) {
    super("MCU Version", FILTER_TYPE.DEVICE.MCU_VERISON, [DATA_SCOPES.DEVICE], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.mcu_version;
  }
}

export class CellIdFilter extends StringFilter {
  constructor(visibility, restrictedToScope) {
    super("Cell ID", FILTER_TYPE.DEVICE.CELL_UID, [DATA_SCOPES.DEVICE], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.cell_uid;
  }
}

export class DeviceNameFilter extends StringFilter {
  constructor(visibility, restrictedToScope) {
    super("Device Name", FILTER_TYPE.DEVICE.NAME, [DATA_SCOPES.ALL], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return getDisplayName(device);
  }
}

export class OnDeviceNameFilter extends StringFilter {
  constructor(visibility, restrictedToScope) {
    super("On Device Name", FILTER_TYPE.DEVICE.ORIGINAL_NAME, [DATA_SCOPES.ALL], "Name associated with the device locally. Used to ssh into the device.", visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.device_name;
  }
}

export class LocationNameFilter extends StringFilter {
  constructor(visibility, restrictedToScope) {
    super("Location Name", FILTER_TYPE.DEVICE.LOCATION_NAME, [DATA_SCOPES.ALL], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.location_name;
  }
}

export class LocationDescriptionFilter extends StringFilter {
  constructor(visibility, restrictedToScope) {
    super("Location Description", FILTER_TYPE.DEVICE.LOCATION_DESC, [DATA_SCOPES.ALL], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.location_description;
  }
}

export class GroupsFilter extends ListFilter {
  constructor(visibility, restrictedToScope) {
    super("Device Groups", FILTER_TYPE.DEVICE.GROUPS, [DATA_SCOPES.ALL], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return deviceGroups && deviceGroups.length > 0 ? deviceGroups : ["None"];
  }
}

export class DeviceIDFilter extends NumberFilter {
  constructor(visibility, restrictedToScope) {
    super("Device ID", FILTER_TYPE.DEVICE.ID, [DATA_SCOPES.ALL], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.device_id;
  }
}

export class BatteryVoltageFilter extends NumberFilter {
  constructor(visibility, restrictedToScope) {
    super("Battery Voltage", FILTER_TYPE.DEVICE.BATTERY_VOLTAGE, [DATA_SCOPES.ALL], "The last recorded battery voltage on the device. Sentinel function consistently above 3.2v. Only available on cell devices.", visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.battery_voltage || device.battery_voltage === 0 ? parseFloat(device.battery_voltage).toFixed(2) : undefined ;
  }
}

export class LatitudeFilter extends NumberFilter {
  constructor(visibility, restrictedToScope) {
    super("Latitude", FILTER_TYPE.DEVICE.LATITUDE, [DATA_SCOPES.ALL], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.latitude;
  }
}

export class LongitudeFilter extends NumberFilter {
  constructor(visibility, restrictedToScope) {
    super("Longitude", FILTER_TYPE.DEVICE.LONGITUDE, [DATA_SCOPES.ALL], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.longitude;
  }
}

export class CycleTimeFilter extends NumberFilter {
  constructor(visibility, restrictedToScope) {
    super("Cycle Time", FILTER_TYPE.DEVICE.CYCLE_TIME, [DATA_SCOPES.DEVICE], "Maximum time between wake up cycles.", visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.cycle_time;
  }
}

export class TriggerNumberFilter extends NumberFilter {
  constructor(visibility, restrictedToScope) {
    super("Trigger Number", FILTER_TYPE.DEVICE.TRIGGER_NUMBER, [DATA_SCOPES.DEVICE], "Number of backlog images before device wakes up.", visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device.trigger_number;
  }
}

export class MemoryRemainingFilter extends NumberFilter {
  constructor(visibility, restrictedToScope) {
    super("Memory Remaining (MB)", FILTER_TYPE.DEVICE.MEMORY_REMAINING, [DATA_SCOPES.DEVICE], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device?.summary?.space_remaining || device.memory_remaining || null;
  }
}

export class LastInsightInHoursFilter extends NumberFilter {
  constructor(visibility, restrictedToScope) {
    super("Hours Since Last Insight", FILTER_TYPE.DEVICE.LAST_INSIGHT_HOUR, [DATA_SCOPES.ALL], "Hours since we received an insight from this Sentinel.", visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return insightMetrics.lastInsightMS === Infinity ? null : Math.ceil(insightMetrics.lastInsightMS / (1000 * 60 * 60));
  }
}

export class LastImageInHoursFilter extends NumberFilter {
  constructor(visibility, restrictedToScope) {
    super("Hours Since Last Image", FILTER_TYPE.DEVICE.LAST_IMAGE_HOUR, [DATA_SCOPES.ALL], "Hours since last image according to the image file's creation time, which is based on the trail camera's time. This only includes images that were not ignored on device.", visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return insightMetrics.lastImageMS === Infinity ? null : Math.ceil(insightMetrics.lastImageMS / (1000 * 60 * 60));
  }
}

export class CycleCountFilter extends NumberFilter {
  constructor(visibility, restrictedToScope) {
    super("Cycle Count", FILTER_TYPE.DEVICE.CYCLE_COUNT, [DATA_SCOPES.DEVICE], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device?.summary?.cycle_count || null;
  }
}

export class HoursSinceLastCameraTriggerFilter extends NumberFilter {
  constructor(visibility, restrictedToScope) {
    super("Hours Since Last Camera Trigger", FILTER_TYPE.DEVICE.LAST_CYCLE_COUNT_HOUR, [DATA_SCOPES.DEVICE], "Hours since the last increase in camera trigger count. This data is based on summary metrics and can be a few days stale.", visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device?.summary?.hours_since_last_camera_trigger || null;
  }
}

export class ImagesProcessedFilter extends NumberFilter {
  constructor(visibility, restrictedToScope) {
    super("Images Processed", FILTER_TYPE.DEVICE.IMAGES_PROCESSED, [DATA_SCOPES.DEVICE], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return device?.summary?.image_count || device.images_processed || null;
  }
}

export class LastSeenFilter extends DateFilter {
  constructor(visibility, restrictedToScope) {
    super("Last Seen (UTC)", FILTER_TYPE.DEVICE.LAST_SEEN, [DATA_SCOPES.ALL], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    return getFormattedTimeString(device.last_seen);
  }
}

export class DeviceUpdateStatusFilter extends ElementFilter {
  constructor(visibility, restrictedToScope) {
    super("Update Status", FILTER_TYPE.DEVICE.UPDATE_STATUS, [DATA_SCOPES.ALL], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    const status = device.device_update_status;
    let dataMap = {};
    switch (status) {
      case DEVICE_UPDATE_STATUS.SUCCESS: dataMap.element = (<Tooltip title={"Device update passed"}><PassedIcon style={{ color: 'green' }} /></Tooltip>); dataMap.orderValue = 1; break;
      case DEVICE_UPDATE_STATUS.FAILED: dataMap.element = (<Tooltip title={"Device update failed"}><FailedIcon style={{ color: 'red' }} /></Tooltip>); dataMap.orderValue = 2; break;
      case DEVICE_UPDATE_STATUS.PENDING: dataMap.element = (<Tooltip title={"Device update pending"}><PendingIcon style={{ color: '#ebc934' }} /></Tooltip>); dataMap.orderValue = 3; break;
      default: dataMap.element = (<Tooltip title={"No current or past device updates"}><NoActionIcon style={{ color: 'grey' }} /></Tooltip>); dataMap.orderValue = 4; break;
    }
    return dataMap;
  }
}

export class BatteryStatusFilter extends ElementFilter {
  constructor(visibility, restrictedToScope) {
    super("Battery Status", FILTER_TYPE.DEVICE.BATTERY_STATUS, [DATA_SCOPES.ALL], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    let dataMap = {};
    const voltage = device.battery_voltage;
    if (voltage) {
      if (voltage > 4) {
        dataMap.element = (<Tooltip title={"Battery levels are good."}><BatteryFullIcon style={{ color: 'green' }} /></Tooltip>);
        dataMap.orderValue = 1;
      } else if (voltage > 3.8) {
        dataMap.element = (<Tooltip title={"Battery levels are good."}><Battery80Icon style={{ color: 'green' }} /></Tooltip>);
        dataMap.orderValue = 2;
      } else if (voltage > 3.6) {
        dataMap.element = (<Tooltip title={"Battery levels are good."}><Battery60Icon style={{ color: 'green' }} /></Tooltip>);
        dataMap.orderValue = 3;
      } else if (voltage > 3.4) {
        dataMap.element = (<Tooltip title={"Battery levels are good."}><Battery50Icon style={{ color: 'green' }} /></Tooltip>);
        dataMap.orderValue = 4;
      } else if (voltage > MINIMUM_BATTERY_VOLTAGE) {
        dataMap.element = (<Tooltip title={"Battery levels are good."}><Battery30Icon style={{ color: 'green' }} /></Tooltip>);
        dataMap.orderValue = 5;
      } else {
        dataMap.element = (<Tooltip title={"Battery levels are Low. Please replace batteries."}><LowBatteryIcon style={{ color: 'red' }} /></Tooltip>);
        dataMap.orderValue = 6;
      }
    } else {
      dataMap.element = (<Tooltip title={"No Battery Information"}><BatteryFullIcon style={{ color: 'grey' }} /></Tooltip>);
      dataMap.orderValue = 7;
    }
    return dataMap;
  }
}

export class MapButtonFilter extends ElementFilter {
  constructor(visibility, restrictedToScope) {
    super("Map", FILTER_TYPE.DEVICE.MAP_BUTTON, [DATA_SCOPES.ALL], null, visibility, restrictedToScope)
  }

  formatData(device, deviceGroups, insight, insightMetrics, update, alert) {
    let dataMap = {};
    dataMap.element = (<InsightMapDialog disabled={!device.latitude || !device.longitude} insight={device} />);
    if (!!device.latitude && !!device.longitude) {
      dataMap.orderValue = 1;
    } else {
      dataMap.orderValue = 2;
    }
    return dataMap;
  }
}